/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 12;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (!productId || !/^\d+$/.test(String(productId))) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(String(productId));
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  /**
   * Keep only product IDs that still resolve in the storefront.
   * @param {string[]} validIds
   */
  static pruneTo(validIds) {
    const allowed = new Set((validIds || []).map(String));
    const next = this.getProducts().filter((/** @type {string} */ id) => allowed.has(String(id)));
    if (next.length === 0) {
      this.clearProducts();
      return;
    }
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(next.slice(0, this.#MAX_PRODUCTS)));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((/** @type {unknown} */ id) => /^\d+$/.test(String(id))).map(String);
    } catch {
      return [];
    }
  }
}
